import React, { createContext, useContext, useMemo } from "react";
import { useAuth } from "./AuthContext";

const PermissionsContext = createContext(null);

// Prioridade de fallback quando a rota atual e negada (a primeira visivel ganha).
const FALLBACK_ORDER = [
  "/dashboard",
  "/tickets",
  "/assets",
  "/collaborators",
  "/stock",
  "/minha-atividade",
  "/perfil",
];

const isAdminUser = (user) =>
  user?.profile?.toLowerCase() === "administrador" ||
  user?.profile?.toLowerCase() === "admin" ||
  !!user?.is_platform_admin;

export const PermissionsProvider = ({ children }) => {
  const { user, loading } = useAuth();

  const value = useMemo(() => {
    const isAdmin = isAdminUser(user);
    const list = Array.isArray(user?.screen_permissions)
      ? user.screen_permissions
      : [];
    const byRoute = new Map(list.map((p) => [p.route, p]));

    // Acha a permissao de uma rota: match exato, senao a do "pai" mais especifico
    // (ex.: /tickets/5 herda de /tickets). Mantem o padrao do BenDash.
    const lookup = (route) => {
      if (byRoute.has(route)) return byRoute.get(route);
      let best = null;
      for (const [r, p] of byRoute.entries()) {
        if (route === r || route.startsWith(`${r}/`)) {
          if (!best || r.length > best.r.length) best = { r, p };
        }
      }
      return best ? best.p : null;
    };

    const can = (action, route) => {
      if (isAdmin) return true;
      const p = lookup(route);
      if (!p) return action === "view"; // rota desconhecida: visivel por padrao
      return !!p[`can_${action}`];
    };

    const visible = new Set(
      list.filter((p) => p.can_view).map((p) => p.route)
    );

    const firstVisible =
      FALLBACK_ORDER.find((r) => can("view", r)) || "/perfil";

    return { ready: !loading, isAdmin, can, visible, firstVisible };
  }, [user, loading]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    // Fallback permissivo se usado fora do provider (nao deve acontecer).
    return {
      ready: true,
      isAdmin: true,
      can: () => true,
      visible: new Set(),
      firstVisible: "/dashboard",
    };
  }
  return ctx;
};
